import { green } from '@ant-design/colors';
import { CheckCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
  Form,
  Input,
  Select,
  Space,
  Spin,
  Table,
  Tooltip,
  Typography
} from 'antd';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import CustomDatePicker from '../../components/CustomDatePicker';

const EditableCell = ({
  editing,
  dataIndex,
  record,
  children,
  editableCell
}) => {
  let inputNode = null;
  const optionsSelect = null;

  switch (dataIndex && record[dataIndex]?.type) {
    case 'input':
      inputNode = <Input />;
      break;
    case 'select':
      inputNode = <Select>{optionsSelect}</Select>;
      break;
    case 'date':
      inputNode = (
        <CustomDatePicker
          style={{ width: '100%' }}
          placeholder="DD/MM/YYYY"
          format="DD/MM/YYYY"
        />
      );
      break;
    default:
      inputNode = <Input />;
  }

  const nameCol = dataIndex ? record[dataIndex]?.name : null;
  const content =
    dataIndex === 'emision' ? <strong>{children}</strong> : children;

  return (
    <td>
      {editing && nameCol && editableCell ? (
        <Form.Item
          name={[dataIndex, nameCol]}
          style={{ margin: 0 }}
          rules={[{ required: true, message: 'Requerido' }]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        content
      )}
    </td>
  );
};

const AutorizacionDatosFiscal = ({
  autorizacion,
  datosConfirmados,
  setDatosConfirmados,
  setHabilitarBotonAprobar,
  form
}) => {
  const [loading, setLoading] = useState(true);
  const [fiscalDatos, setFiscalDatos] = useState([]);
  const [editableEmisionFiscal, setEditableEmisionFiscal] = useState(true);

  const renderColumnFiscal = (record, index) => {
    let { value } = record;
    if (record.requiereRevision) {
      return (
        <Tooltip title="Este dato  requiere autorización">
          <Typography.Text type="warning">
            <span>
              {record.value}
              <InfoCircleOutlined
                style={{
                  color: 'rgba(0,0,0,.45)',
                  marginRight: '5px'
                }}
              />
            </span>
          </Typography.Text>
        </Tooltip>
      );
    }

    if (record.actualizado || (!editableEmisionFiscal && index === 'emision')) {
      const datoFormateado =
        record.type === 'date' ? moment(value).format('DD/MM/YYYY') : value;
      value = <strong>{datoFormateado}</strong>;
    }
    return value;
  };

  const components = {
    body: {
      cell: EditableCell
    }
  };
  const columnsFiscal = [
    {
      title: '',
      dataIndex: 'label',
      key: 'label'
    },
    {
      title: 'Control Sistema',
      dataIndex: 'controlSegu',
      key: 'controlSegu',
      render: renderColumnFiscal
    },

    {
      title: 'Solicitud (Usuario)',
      dataIndex: 'solicitud',
      key: 'solicitud'
    },
    {
      title: (
        <>
          <Space>
            Datos para emisión
            <Tooltip title="Datos confirmados">
              <Typography.Text hidden={!datosConfirmados}>
                <CheckCircleOutlined
                  style={{
                    color: green.primary
                  }}
                />
              </Typography.Text>
            </Tooltip>
          </Space>
        </>
      ),
      dataIndex: 'emision',
      key: 'emision',
      width: 300,
      render: (record) => renderColumnFiscal(record, 'emision'),
      editable: editableEmisionFiscal
    }
  ];

  const columns = columnsFiscal.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: col.dataIndex === 'emision' ? record.emision.type : '',
        dataIndex: col.dataIndex,
        title: col.title,
        editing: true,
        editableCell: editableEmisionFiscal
      })
    };
  });

  const cargarDatosFiscal = useCallback(() => {
    const autorizacionFiscal = autorizacion.detalle?.fiscal;
    const fiscalDatosList = [
      {
        key: 'ruc',
        label: 'RUC',
        controlSegu: {
          requiereRevision: autorizacionFiscal.set?.rucRequiereRevision,
          value: autorizacionFiscal.set?.ruc
        },

        solicitud: autorizacionFiscal.solicitud?.ruc,
        emision: {
          type: 'input',
          name: 'ruc',
          value: autorizacionFiscal.emision?.ruc,
          actualizado: autorizacionFiscal.emision?.actualizado || false
        }
      },
      {
        key: 'razonSocial',
        label: 'Razón Social',
        controlSegu: {
          requiereRevision: autorizacionFiscal.set?.razonSocialRequiereRevision,
          value: autorizacionFiscal.set?.razonSocial
        },
        solicitud: autorizacionFiscal.solicitud?.razonSocial,
        emision: {
          type: 'input',
          name: 'razonSocial',
          value: autorizacionFiscal.emision?.razonSocial,
          actualizado: autorizacionFiscal.emision?.actualizado || false
        }
      }
    ];
    // Se modifica el estado de los datos que se maneja dentro del componente
    setFiscalDatos(fiscalDatosList);

    const initialValues = {};
    fiscalDatosList.forEach((data) => {
      if (data.emision && data.emision.name && data.emision.value) {
        if (data.emision.type !== 'date') {
          initialValues[data.emision.name] = data.emision.value;
        }
      }
    });
    form.setFieldsValue({
      emision: { ...initialValues }
    });
    setLoading(false);
  }, [autorizacion.detalle?.fiscal, form]);

  useEffect(() => {
    if (datosConfirmados) {
      setEditableEmisionFiscal(false);
    } else {
      setEditableEmisionFiscal(true);
    }
  }, [datosConfirmados]);

  useEffect(() => {
    if (autorizacion && autorizacion.detalle?.fiscal) {
      cargarDatosFiscal();
      if (autorizacion.estado !== 'PENDIENTE') {
        setEditableEmisionFiscal(false);
      }
    }
  }, [
    autorizacion,
    cargarDatosFiscal,
    setDatosConfirmados,
    setHabilitarBotonAprobar
  ]);

  return (
    <div style={{ overflowX: 'auto', textAlign: 'center' }}>
      <Spin
        spinning={loading}
        size="small"
        tip="Cargando..."
        style={{ overflowX: 'auto', textAlign: 'center' }}
      >
        {fiscalDatos && (
          <>
            <Form form={form} component={false}>
              <Table
                components={components}
                dataSource={fiscalDatos}
                columns={columns}
                rowClassName={(record) =>
                  record.controlSegu.requiereRevision
                    ? 'ant-table-row-selected'
                    : ''
                }
                pagination={false}
                size="small"
                style={{ overflowX: 'auto' }}
                rowKey={(record) => record.key}
              />
              <br />
            </Form>
          </>
        )}
      </Spin>
    </div>
  );
};

export default AutorizacionDatosFiscal;
